<script lang="ts">
import GenericButton from "@/Components/Generics/GenericButton.vue";
import GenericInput from "@/Components/Generics/GenericInput.vue";
import SelectableButton from "@/Components/Generics/SelectableButton.vue";
import MainPage from "@/Components/MainPage.vue";
import { defineComponent } from "vue";

export default defineComponent({
    name: "SearchCompanyVue",
    components: {
        MainPage,
        GenericButton,
        SelectableButton,
        GenericInput,
    },
    props: {
        searchingProcess: {
            type: Boolean,
            default: false,
        },
        searchString: {
            type: String,
            required: true,
        },
    },
    emits: ["clickSearchCompany", "changeInputValue"],
    data: () => ({}),
    computed: {},
    methods: {
        selectSector(num: number): void {
            const trans = this.$trans(`search.help-4-${num}`);
            this.onInput(trans);
        },
        onInput(newVal: string): void {
            this.$emit("changeInputValue", newVal);
        },
    },
});
</script>

<template>
    <MainPage :title="$trans('navigation.nav_search')" :title-id="'startOfJobfuchs'" :gap="true">
        <div id="startOfSearchCompany" class="page-section flex flex--width flex--v flex--valign-c flex--gap-50">
            <div class="flex flex--v flex--valign-c flex--gap-30">
                <!-- TITLE -->
                <div class="flex flex--v flex--valign-c flex--gap-10">
                    <span class="text text--primary-gradient text--bold text--size-30 text--center">
                        {{ $trans("search.step-1-headline") }}
                    </span>
                    <span class="text text--accent-gray-b text--medium text--size-20 text--less-gap text--center">
                        {{ $trans("search.step-1-subheadline") }}
                    </span>
                </div>

                <!-- INFO -->
                <div class="flex flex--v flex--valign-c flex--gap-10">
                    <div class="flex flex--v flex--valign-c flex--gap-15">
                        <span class="text text--accent-gray-b text--medium text--size-20 text--height-24 text--less-gap text--center">
                            {{ $trans("search.help-1") }}
                        </span>
                        <span class="text text--accent-gray-b text--medium text--size-20 text--height-24 text--less-gap text--center">
                            {{ $trans("search.help-2") }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- INPUT -->
            <div class="input-section flex flex--width flex--gap-20">
                <GenericInput class="text text--primary-b text--semi-bold text--size-22" :value="searchString" :placeholder="$trans('search.search_placeholder')" :disabled="searchingProcess" @input="onInput" />

                <GenericButton class="search-btn" :title="$trans('search.search_button')" :identifier="'djf_update_search'" :searching="searchingProcess" :secondary="true" :omit-icon-when-s-m="false" @click="$emit('clickSearchCompany')" />
            </div>

            <!-- HELP -->
            <div class="flex flex--v flex--valign-c flex--gap-30">
                <div class="flex flex--v flex--valign-c flex--gap-20">
                    <span class="text text--accent-gray-b text--medium text--size-20 text--height-32 text--less-gap text--center">
                        {{ $trans("search.help-3-1") }}
                        <br />
                        {{ $trans("search.help-3-2") }}
                    </span>
                    <div class="selectable-buttons flex flex--halign-c flex--valign-s flex--gap-10 flex--wrap">
                        <SelectableButton v-for="number in 12" :key="number" class="selectable-button" :title="$trans(`search.help-4-${number}`).toUpperCase()" :circular="true" @click="selectSector(number)" />
                    </div>
                </div>
            </div>
            <div class="flex flex--v flex--valign-c flex--gap-30">
                <div class="flex flex--v flex--valign-c flex--gap-20">
                    <span class="text text--accent-gray-b text--medium text--size-20 text--height-24 text--less-gap text--center">
                        {{ $trans("search.help-5") }}
                    </span>
                    <span class="text text--accent-gray-b text--medium text--size-20 text--height-32 text--less-gap text--center">
                        {{ $trans("search.help-6-1") }}
                        <br />
                        {{ $trans("search.help-6-2") }}
                    </span>
                </div>
            </div>

            <!-- END -->
            <div class="flex flex--v flex--valign-c flex--gap-10">
                <span class="text text--primary-gradient text--bold text--size-30 text--center">
                    {{ $trans("search.help-7-1") }}
                </span>
                <span class="text text--accent-gray-b text--medium text--size-20 text--less-gap text--center">
                    {{ $trans("search.help-7-2") }}
                </span>
            </div>
        </div>
    </MainPage>
</template>

<style lang="scss" scoped>
.search-btn {
    min-width: fit-content;
}
.selectable-buttons {
    max-width: calc(100% - #{rem(250)});

    .selectable-button {
        padding: rem(0) rem(20);
        height: rem(35);
    }
}

@include media-breakpoint-down(xl) {
    .page-section {
        gap: rem(45);

        .selectable-buttons {
            max-width: calc(100% - #{rem(200)});
        }
    }
}
@include media-breakpoint-down(lg) {
    .page-section {
        gap: rem(45);

        .selectable-buttons {
            max-width: calc(100% - #{rem(150)});

            .selectable-button {
                padding: rem(0) rem(20);
                height: rem(32);
            }
        }
    }
}
@include media-breakpoint-down(md) {
    .page-section {
        gap: rem(35);

        .input-section {
            flex-direction: column;
            align-items: center;

            .search-btn {
                width: fit-content;
            }
        }

        .selectable-buttons {
            max-width: calc(100% - #{rem(100)});

            .selectable-button {
                padding: rem(0) rem(18);
                height: rem(30);
            }
        }
    }
}
@include media-breakpoint-down(sm) {
    .page-section {
        gap: rem(25);

        .selectable-buttons {
            max-width: calc(100% - #{rem(50)});

            .selectable-button {
                padding: rem(0) rem(15);
                height: rem(25);
            }
        }
    }
}
</style>
